import {
  isAddonProduct,
  isMealboxProduct,
  isStandaloneProduct,
} from "@chef/helpers";
import { language } from "@chef/constants";

import {
  IDeviationProduct,
  selectAddonProducts,
  selectDeviationProducts,
  updateAddonToDeviation,
  setAddonProducts,
  useUpdateBillingAgreementMutation,
  useBillingQuery,
  showNotification,
  createPayloadProductsArray,
  useProductCategoriesQuery,
  useProductsByCategoriesQuery,
} from "..";

import { useAppDispatch, useAppSelector } from "./useApp";
import { useTrack } from "./useTrack";
import { getStandaloneProductCategoryIdsFromProductCategories } from "../helpers";

type UseAddonsAndGroceriesBasketArgs =
  | {
      type: "subscription";
      week?: never;
      year?: never;
    }
  | {
      type: "week";
      week: number;
      year: number;
    };

export const intl = (
  {
    no: {
      GENERIC_ERROR_MESSAGE: "Noe gikk galt, kontakt support",
    },
    se: {
      GENERIC_ERROR_MESSAGE: "Något gick fel, försök igen senare",
    },
    dk: {
      GENERIC_ERROR_MESSAGE: "Noget gik galt, kontakt support",
    },
  } as const
)[language];

export const useAddonsAndGroceriesBasket = ({
  type,
  week,
  year,
}: UseAddonsAndGroceriesBasketArgs) => {
  const dispatch = useAppDispatch();
  const track = useTrack();

  const { data: billingQuery, isFetching: isFetchingBilling } = useBillingQuery(
    undefined,
    {
      skip: type !== "week",
    },
  );

  const { data: productCategoriesData } = useProductCategoriesQuery(undefined, {
    skip: type !== "week",
  });

  const { data: standaloneProductsByCategoriesData } =
    useProductsByCategoriesQuery(
      {
        categoryIds: getStandaloneProductCategoryIdsFromProductCategories(
          productCategoriesData?.productCategories || [],
        ),
        week: week!,
        year: year!,
      },
      { skip: type !== "week" || (type === "week" && !productCategoriesData) },
    );

  const [updateBillingMutation, { isLoading: isSubmittingBilling }] =
    useUpdateBillingAgreementMutation();

  const basket =
    useAppSelector(
      type === "week"
        ? selectDeviationProducts({ week, year })
        : selectAddonProducts,
    ) ?? [];

  const billing = billingQuery?.billing;
  const subscribedProducts = billing?.baskets
    .find((basket) => basket.default)
    ?.basketProducts.filter(
      (basketProduct) =>
        isStandaloneProduct(basketProduct.variation.product) ||
        isAddonProduct(basketProduct.variation.product),
    );

  const isBillingLoading = isFetchingBilling || isSubmittingBilling;

  const addOrRemoveProduct = (product: IDeviationProduct) => {
    if (type === "week") {
      dispatch(updateAddonToDeviation({ week, year, product }));
    }

    if (type === "subscription") {
      const productShouldBeRemovedFromBasket = product.quantity === 0;

      if (productShouldBeRemovedFromBasket) {
        return dispatch(
          setAddonProducts(
            basket.filter(
              (basketProduct) => basketProduct.productId !== product.productId,
            ),
          ),
        );
      }

      const productExistsInBasket = basket?.some(
        (basketProduct) => basketProduct.productId === product.productId,
      );

      if (!productExistsInBasket) {
        return dispatch(setAddonProducts([...basket, product]));
      }

      return dispatch(
        setAddonProducts(
          basket.map((basketProduct) => {
            if (basketProduct.productId !== product.productId) {
              return basketProduct;
            }

            return product;
          }),
        ),
      );
    }
  };

  const addProductToSubscription =
    type === "subscription"
      ? undefined
      : async (updatedProduct: IDeviationProduct) => {
          let addonProducts =
            subscribedProducts?.map((p) => {
              return { variationId: p.variationId, quantity: p.quantity };
            }) || [];

          const shouldBeRemoved = updatedProduct.quantity === 0;

          const oldQuantity =
            addonProducts.find(
              (basketProduct) =>
                basketProduct.variationId === updatedProduct.variationId,
            )?.quantity || 0;

          const productExistsInBasket = oldQuantity > 0;

          if (shouldBeRemoved) {
            addonProducts = addonProducts.filter(
              (p) => p.variationId !== updatedProduct.variationId,
            );
          } else if (!productExistsInBasket) {
            addonProducts = [
              ...addonProducts,
              {
                variationId: updatedProduct.variationId,
                quantity: updatedProduct.quantity,
              },
            ];
          } else {
            addonProducts = addonProducts.map((p) => {
              if (p.variationId !== updatedProduct.variationId) {
                return p;
              }

              return {
                variationId: updatedProduct.variationId,
                quantity: updatedProduct.quantity,
              };
            });
          }

          if (!billing) {
            console.error("submit called before billing loaded");
            return;
          }

          try {
            const defaultBasket = billing.baskets?.find((b) => b.default);

            const mealboxBasketProduct = defaultBasket?.basketProducts.find(
              (bp) => isMealboxProduct(bp.variation.product),
            );

            if (!mealboxBasketProduct) {
              console.error(
                "Attempted to update addons subscription without existing mealboxProduct in billing",
              );
              showNotification({
                type: "error",
                message: intl.GENERIC_ERROR_MESSAGE,
              });
              return;
            }

            await updateBillingMutation({
              status: billing.status,
              basket: {
                shippingAddress: defaultBasket?.shippingAddressId,
                timeblock: defaultBasket?.timeblockId,
                subscribedDeliveryInterval:
                  defaultBasket?.deliveryInterval.toString(),
                isDefault: true,
                isActive: true,
                basketProducts: [
                  {
                    subscribedQuantity: mealboxBasketProduct.quantity,
                    subscribedVariationId: mealboxBasketProduct.variationId,
                  },
                  ...addonProducts.map(({ variationId, quantity }) => ({
                    subscribedQuantity: quantity,
                    subscribedVariationId: variationId,
                  })),
                ],
              },
              _suppressEndpointReload: true,
            }).unwrap();

            if (!updatedProduct) {
              return;
            }

            const productData = createPayloadProductsArray(
              [updatedProduct],
              undefined,
              standaloneProductsByCategoriesData?.productsByCategories,
            )?.[0];

            if (!productData) {
              return;
            }

            track("subscriptionAddonsChanged", {
              affiliation: "Frontend process",
              source: "weekEditor",
              product: {
                category: productData.category,
                product_id: productData.product_id,
                name: productData.name,
                price: productData.price,
                category_id: productData.category_id,
                variant_id: productData.variant_id,
                variant: productData.variant,
              },
              newQuantity: updatedProduct.quantity,
              oldQuantity,
            });
          } catch (err) {
            console.error(err);
          }
        };

  return {
    basket,
    addOrRemoveProduct,
    addProductToSubscription,
    subscribedProducts,
    isBillingLoading,
  };
};
